// frontend/src/components/boarding/QRCodeScanner.js
import React, { useState, useEffect, useRef } from 'react';
import { X, Camera } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import jsQR from 'jsqr';

/**
 * QRCodeScanner
 *
 * Camera-based QR code scanner for drivers
 * Uses device camera to scan boarding pass QR codes
 * Provides real-time feedback and validation
 */
const QRCodeScanner = ({ open, onClose, onScanSuccess, onScanError }) => {
  const { t } = useTranslation();
  const { background, text, cn } = useTheme();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const streamRef = useRef(null);
  const scanningRef = useRef(false); // Use ref instead of state for scanning flag

  const [error, setError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);

  /**
   * Start camera and scanning when modal opens
   */
  useEffect(() => {
    if (open) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [open]);

  /**
   * Start device camera
   */
  const startCamera = async () => {
    setError(null);
    setCameraReady(false);

    try {
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Camera API not available. Please use HTTPS or a modern browser.');
        return;
      }

      // Check if running in secure context (HTTPS or localhost)
      if (!window.isSecureContext && window.location.hostname !== 'localhost') {
        setError('Camera requires HTTPS connection. Please access this page securely.');
        return;
      }

      // Request camera permission
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' } // Use back camera on mobile
      });

      streamRef.current = stream;

      if (videoRef.current) {
        const video = videoRef.current;

        // Set up video element
        video.srcObject = stream;
        video.setAttribute('playsinline', true); // iOS compatibility
        video.setAttribute('autoplay', true); // Ensure autoplay
        video.setAttribute('muted', true); // Muted required for autoplay on some browsers

        console.log('Stream obtained, waiting for video to load...');

        // Wait for video metadata to load
        const metadataPromise = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Video metadata timeout'));
          }, 10000); // 10 second timeout

          video.onloadedmetadata = () => {
            clearTimeout(timeout);
            console.log('Video metadata loaded:', video.videoWidth, 'x', video.videoHeight);
            resolve();
          };
        });

        try {
          // Wait for metadata to load
          await metadataPromise;

          // Start playing
          await video.play();

          console.log('Video playing successfully');
          setCameraReady(true);
          scanningRef.current = true; // Set scanning flag synchronously

          // Start scanning loop
          requestAnimationFrame(scanQRCode);
        } catch (playError) {
          console.error('Video play error:', playError);
          setError('Failed to start video playback: ' + playError.message);

          // Clean up stream if play fails
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
        }
      }
    } catch (err) {
      console.error('Camera error:', err);

      if (err.name === 'NotAllowedError') {
        setError(t('cameraPermissionDenied') || 'Camera permission denied. Please allow camera access in browser settings.');
      } else if (err.name === 'NotFoundError') {
        setError(t('cameraNotAvailable') || 'No camera found on this device.');
      } else if (err.name === 'NotSupportedError' || err.name === 'NotReadableError') {
        setError('Camera is in use by another application or browser tab.');
      } else if (err.name === 'SecurityError') {
        setError('Camera access blocked. Please use HTTPS or check browser security settings.');
      } else {
        setError(`Unable to access camera: ${err.message || 'Unknown error'}`);
      }
    }
  };

  /**
   * Stop camera stream
   */
  const stopCamera = () => {
    // Stop scanning flag
    scanningRef.current = false;

    // Stop animation loop
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Stop video stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Clear video element
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraReady(false);
  };

  /**
   * Scan QR code from video feed
   */
  const scanQRCode = () => {
    if (!videoRef.current || !canvasRef.current || !scanningRef.current) {
      console.log('Scanning stopped:', {
        hasVideo: !!videoRef.current,
        hasCanvas: !!canvasRef.current,
        scanning: scanningRef.current
      });
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Check if video is ready
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      // Set canvas size to match video
      canvas.height = video.videoHeight;
      canvas.width = video.videoWidth;

      // Draw current video frame to canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Get image data
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

      // Scan for QR code with more permissive options
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'attemptBoth'  // Try both normal and inverted colors
      });

      if (code) {
        // QR code detected
        console.log('QR Code detected! Raw data:', code.data);
        handleQRCodeDetected(code.data);
        return; // Stop scanning after successful detection
      }
    }

    // Continue scanning loop
    animationFrameRef.current = requestAnimationFrame(scanQRCode);
  };

  /**
   * Handle QR code detection
   */
  const handleQRCodeDetected = (qrData) => {
    try {
      console.log('Attempting to parse QR data:', qrData);

      // Parse QR code data (should be JSON)
      const parsedData = JSON.parse(qrData);
      console.log('Parsed QR data:', parsedData);

      // Validate QR code structure
      if (parsedData.code && parsedData.bookingId && parsedData.entityId) {
        console.log('Valid boarding pass detected!');
        // Valid boarding pass QR code
        stopCamera();

        if (onScanSuccess) {
          onScanSuccess(parsedData);
        }
      } else {
        // Invalid QR code format
        console.error('Missing required fields:', {
          hasCode: !!parsedData.code,
          hasBookingId: !!parsedData.bookingId,
          hasEntityId: !!parsedData.entityId
        });
        throw new Error('Invalid boarding pass QR code - missing required fields');
      }
    } catch (err) {
      console.error('QR code parse error:', err);
      console.error('Raw QR data was:', qrData);

      // Invalid QR code
      stopCamera();

      if (onScanError) {
        onScanError(t('invalidBoardingPass') || 'Invalid boarding pass QR code');
      }
    }
  };

  /**
   * Handle manual close
   */
  const handleClose = () => {
    stopCamera();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className={cn("max-w-md w-full rounded-lg shadow-xl", background.secondary)}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-blue-500" />
            <h2 className={cn("text-xl font-semibold", text.primary)}>{t('scanQRCode')}</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            </div>
          )}

          {!error && (
            <div>
              {/* Camera viewfinder (video mounts immediately so getUserMedia can bind) */}
              <div className="relative w-full bg-black rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  className="w-full h-auto block"
                  playsInline
                  autoPlay
                  muted
                />

                {/* Spinner overlay while waiting for cameraReady */}
                {!cameraReady && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black bg-opacity-70">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    <p className={cn("text-sm text-white")}>Starting camera...</p>
                  </div>
                )}

                {/* Scanning overlay with corner markers */}
                {cameraReady && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative w-[70%] aspect-square">
                      {/* Shadow overlay */}
                      <div className="absolute inset-0 shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]"></div>

                      {/* Border */}
                      <div className="absolute inset-0 border-3 border-green-500 rounded-lg"></div>

                      {/* Corner indicators */}
                      <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-green-500 rounded-tl"></div>
                      <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-green-500 rounded-tr"></div>
                      <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-green-500 rounded-bl"></div>
                      <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-green-500 rounded-br"></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Hidden canvas for QR code processing */}
              <canvas ref={canvasRef} className="hidden" />

              {/* Instructions */}
              {cameraReady && (
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Camera className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      {t('scannerActive')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            {t('cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QRCodeScanner;
